import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "../shared/Autocomplete";
import { AGENT_SEARCH_COPY, agentToSelection, clearAgentFromLocalStorage, didAgentsLoadFail, ensureAgentsLoaded, findAgentByGuid, getHearAboutUsValue, getPrefilledSelectionFromStorage, hasCommittedAgentSelection, isAgentSearchRequired, isReferredByLoHau, isReturningCustomerHau, onAgentsReady, parseAgentFieldValue, sanitizeAgentNameInput, searchAgentSuggestions, serializeAgentFieldValue, shouldShowAgentSkipControl, shouldShowAgentTypeahead, skippedAgentSelection, syncAgentSelectionToLocalStorage, } from "../../services/agentSearchUtils";
export default function AgentSearch(_a) {
    var field = _a.field, onChange = _a.onChange, onBlur = _a.onBlur, touched = _a.touched, isLastStep = _a.isLastStep;
    var fieldConfig = field.config;
    var _b = useState(false), blurred = _b[0], setBlurred = _b[1];
    var _c = useState(""), inputValue = _c[0], setInputValue = _c[1];
    var _d = useState(null), selection = _d[0], setSelection = _d[1];
    var _e = useState(false), skipped = _e[0], setSkipped = _e[1];
    var _f = useState(false), agentsReady = _f[0], setAgentsReady = _f[1];
    var _g = useState(false), agentsLoadFailed = _g[0], setAgentsLoadFailed = _g[1];
    var prefilledRef = useRef(false);
    var prevHauRef = useRef(null);
    var skippedRef = useRef(false);
    var hearAboutUs = getHearAboutUsValue();
    var showTypeahead = shouldShowAgentTypeahead(hearAboutUs);
    var showSkipCheckbox = shouldShowAgentSkipControl(hearAboutUs);
    var isRequired = isAgentSearchRequired(fieldConfig);
    var selectionCommitted = hasCommittedAgentSelection(selection, inputValue);
    var labelToShow = isLastStep && fieldConfig.labelAlt ? fieldConfig.labelAlt : fieldConfig.label;
    useEffect(function () {
        skippedRef.current = skipped;
    }, [skipped]);
    useEffect(function () {
        var parsed = parseAgentFieldValue(field.fieldValue);
        if (parsed === null || parsed === void 0 ? void 0 : parsed.skipped) {
            setSkipped(true);
            setInputValue("");
            setSelection(null);
            return;
        }
        if (parsed === null || parsed === void 0 ? void 0 : parsed.agentGuid) {
            setSelection(parsed);
            setInputValue(parsed.displayName);
            setSkipped(false);
            syncAgentSelectionToLocalStorage(parsed);
        }
    }, [field.fieldValue]);
    useEffect(function () {
        if (prevHauRef.current === null) {
            prevHauRef.current = hearAboutUs;
            return;
        }
        if (prevHauRef.current === hearAboutUs) {
            return;
        }
        prevHauRef.current = hearAboutUs;
        setBlurred(false);
        setInputValue("");
        setSelection(null);
        setSkipped(false);
        setAgentsReady(false);
        setAgentsLoadFailed(false);
        prefilledRef.current = false;
        onChange("", false);
    }, [hearAboutUs, onChange]);
    useEffect(function () {
        if (!showTypeahead) {
            return;
        }
        var unsubscribe = onAgentsReady(function () {
            setAgentsReady(true);
            setAgentsLoadFailed(didAgentsLoadFail());
        });
        ensureAgentsLoaded();
        return unsubscribe;
    }, [showTypeahead]);
    useEffect(function () {
        if (!agentsReady ||
            prefilledRef.current ||
            field.fieldValue ||
            !showTypeahead ||
            !isReferredByLoHau(hearAboutUs)) {
            return;
        }
        var prefilled = getPrefilledSelectionFromStorage();
        if (prefilled) {
            prefilledRef.current = true;
            setSelection(prefilled);
            setInputValue(prefilled.displayName);
            onChange(serializeAgentFieldValue(prefilled), true);
        }
    }, [agentsReady, showTypeahead, field.fieldValue, hearAboutUs, onChange]);
    var suggestions = useMemo(function () {
        if (!showTypeahead ||
            skipped ||
            !agentsReady ||
            selectionCommitted) {
            return [];
        }
        return searchAgentSuggestions(inputValue);
    }, [showTypeahead, skipped, agentsReady, inputValue, selectionCommitted]);
    var showError = !field.isValid && (touched || blurred);
    var showSuccess = field.isValid && (touched || blurred) && (Boolean(selection === null || selection === void 0 ? void 0 : selection.agentGuid) || skipped);
    var fieldClass = [
        fieldConfig.className || "",
        showError ? "field-error" : "",
        showSuccess ? "field-success" : "",
    ]
        .filter(Boolean)
        .join(" ")
        .trim();
    var commitSelection = function (nextSelection, blur) {
        if (blur === void 0) { blur = false; }
        setSelection(nextSelection);
        setSkipped(false);
        syncAgentSelectionToLocalStorage(nextSelection);
        if (nextSelection) {
            setInputValue(nextSelection.displayName);
            onChange(serializeAgentFieldValue(nextSelection), blur);
        }
        else {
            setInputValue("");
            onChange("", blur);
        }
        if (blur) {
            setBlurred(true);
            onBlur();
        }
    };
    var invalidateInput = function (blur) {
        if (blur === void 0) { blur = false; }
        setSelection(null);
        setSkipped(false);
        setInputValue("");
        onChange("", blur);
        if (blur) {
            setBlurred(true);
            onBlur();
        }
    };
    var handleInputChange = function (value) {
        var cleaned = sanitizeAgentNameInput(value);
        setInputValue(cleaned);
        setSkipped(false);
        setSelection(null);
        onChange("", false);
    };
    var handleSelectSuggestion = function (item) {
        var agent = findAgentByGuid(item.id);
        if (!agent) {
            return;
        }
        var nextSelection = agentToSelection(agent);
        if (!nextSelection) {
            return;
        }
        commitSelection(nextSelection, true);
    };
    var handleAutocompleteBlur = function () {
        setBlurred(true);
        if (skippedRef.current) {
            onBlur();
            return;
        }
        if (isReturningCustomerHau(hearAboutUs) && !inputValue.trim() && !isRequired) {
            onBlur();
            return;
        }
        if (selectionCommitted) {
            onChange(serializeAgentFieldValue(selection), true);
            onBlur();
            return;
        }
        if (inputValue.trim() && !(selection === null || selection === void 0 ? void 0 : selection.agentGuid)) {
            invalidateInput(true);
            return;
        }
        if (!inputValue.trim()) {
            invalidateInput(true);
            return;
        }
        onBlur();
    };
    var handleSkipChange = function (checked) {
        if (!showSkipCheckbox) {
            return;
        }
        if (checked) {
            skippedRef.current = true;
            clearAgentFromLocalStorage();
            var skipSelection = skippedAgentSelection();
            setSkipped(true);
            setSelection(null);
            setInputValue("");
            onChange(serializeAgentFieldValue(skipSelection), true);
            setBlurred(true);
            onBlur();
            return;
        }
        skippedRef.current = false;
        setSkipped(false);
        onChange("", false);
    };
    var containerClass = [
        "textbox",
        "lo-picker"
    ]
        .filter(Boolean)
        .join(" ");
    if (!showTypeahead && !showSkipCheckbox) {
        return null;
    }
    return (_jsxs("div", { className: containerClass, children: [showTypeahead && (_jsxs(_Fragment, { children: [labelToShow ? (_jsxs("label", { htmlFor: fieldConfig.fieldId, children: [labelToShow, isRequired ? " *" : ""] })) : (_jsx("label", { htmlFor: fieldConfig.fieldId, className: "visually-hidden", children: fieldConfig.placeholder || AGENT_SEARCH_COPY.placeholder })), _jsx(Autocomplete, { inputId: fieldConfig.fieldId, value: inputValue, placeholder: fieldConfig.placeholder || AGENT_SEARCH_COPY.placeholder, className: fieldClass, suggestions: suggestions, showSuggestions: !selectionCommitted, disabled: skipped, required: isRequired, ariaInvalid: showError, onChange: handleInputChange, onSelect: handleSelectSuggestion, onBlur: handleAutocompleteBlur }), agentsLoadFailed && (_jsx("p", { className: "lo-picker-helper lo-picker-error", children: AGENT_SEARCH_COPY.loadError }))] })), showSkipCheckbox && (_jsxs("div", { className: "lo-picker-skip-check form-check", onMouseDown: function (e) { return e.preventDefault(); }, children: [_jsx("input", { id: "".concat(fieldConfig.fieldId, "-skip"), type: "checkbox", className: "form-check-input", checked: skipped, onChange: function (e) { return handleSkipChange(e.currentTarget.checked); }, onMouseDown: function (e) { return e.preventDefault(); } }), _jsx("label", { className: "form-check-label", htmlFor: "".concat(fieldConfig.fieldId, "-skip"), onMouseDown: function (e) { return e.preventDefault(); }, children: AGENT_SEARCH_COPY.skip })] }))] }));
}
